import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { MapPin, Shield, Radar, Smartphone, ArrowRight, Check } from 'lucide-react';

const FindMyPage = () => {
  return (
    <div className="pt-24">
      {/* Hero */}
      <section className="py-20 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-green-500/10 border border-green-500/20 mb-6">
              <MapPin className="w-4 h-4 text-green-400" />
              <span className="text-green-400 text-sm font-medium">Works with Apple Find My</span>
            </div>
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold tracking-tight mb-6">
              Never Lose
              <br />
              <span className="text-gray-500">Your Way</span>
            </h1>
            <p className="text-xl text-gray-400 max-w-2xl mx-auto">
              ClearWay integrates with Apple Find My, so you can always locate your
              smart cane using your iPhone, iPad, or Mac.
            </p>
            <p className="text-sm text-gray-500 max-w-2xl mx-auto mt-4">
              Find My is an Apple-only network and requires an iPhone, iPad, or Mac signed
              into iCloud. It is not currently available on Android.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Main Visual */}
      <section className="py-16 px-6">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="relative rounded-3xl overflow-hidden"
          >
            <div className="aspect-[21/9] relative">
              <img
                src="https://images.unsplash.com/photo-1698913461373-bb72ded58f7a?w=1600&q=80"
                alt="Find My integration"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-r from-[#0a0a0a] via-[#0a0a0a]/50 to-transparent" />
              
              {/* Content overlay */}
              <div className="absolute inset-0 flex items-center">
                <div className="max-w-xl ml-8 md:ml-16">
                  <div className="inline-flex items-center gap-3 px-4 py-2 rounded-full bg-green-500/20 border border-green-500/30 mb-6">
                    <div className="w-8 h-8 rounded-full bg-gradient-to-br from-green-400 to-green-600 flex items-center justify-center">
                      <MapPin className="w-4 h-4 text-white" />
                    </div>
                    <span className="text-white font-medium">Works with Find My</span>
                  </div>
                  <h2 className="text-3xl md:text-4xl font-semibold mb-4">
                    Locate from anywhere
                  </h2>
                  <p className="text-gray-300 text-lg">
                    If you misplace your ClearWay, simply open the Find My app 
                    on any Apple device to see its location.
                  </p>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="py-24 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                icon: <Smartphone className="w-7 h-7" />,
                title: 'Works with Apple Devices',
                description: 'Use the Find My app on your iPhone, iPad, or Mac to locate your ClearWay stick anytime.',
                color: 'green'
              },
              {
                icon: <Radar className="w-7 h-7" />,
                title: 'Precision Finding',
                description: 'Get precise direction and distance to your ClearWay when you are nearby using compatible devices.',
                color: 'blue'
              },
              {
                icon: <Shield className="w-7 h-7" />,
                title: 'Private and Secure',
                description: 'Location data is end-to-end encrypted. Only you can see where your ClearWay is located.',
                color: 'purple'
              }
            ].map((feature, index) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="p-8 rounded-2xl bg-gradient-to-b from-gray-800/50 to-transparent border border-gray-800/50"
              >
                <div className={`w-14 h-14 rounded-2xl flex items-center justify-center mb-6 ${
                  feature.color === 'green' ? 'bg-green-500/10 text-green-400' :
                  feature.color === 'blue' ? 'bg-blue-500/10 text-blue-400' :
                  'bg-purple-500/10 text-purple-400'
                }`}>
                  {feature.icon}
                </div>
                <h3 className="text-xl font-semibold mb-3">{feature.title}</h3>
                <p className="text-gray-400 leading-relaxed">{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-24 px-6 bg-[#0f0f0f]">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-semibold mb-4">How It Works</h2>
            <p className="text-gray-400 max-w-2xl mx-auto">
              Finding your ClearWay is as simple as finding your AirPods or AirTag.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-4 gap-8">
            {[
              { step: '01', title: 'Open Find My', description: 'Launch the Find My app on your Apple device' },
              { step: '02', title: 'Select ClearWay', description: 'Find your ClearWay in the Items tab' },
              { step: '03', title: 'View Location', description: 'See the location on a map or get directions' },
              { step: '04', title: 'Use Precision Finding', description: 'Get exact direction and distance when nearby' }
            ].map((item, index) => (
              <motion.div
                key={item.step}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="text-center"
              >
                <div className="w-16 h-16 rounded-full bg-green-500/10 border border-green-500/20 flex items-center justify-center mx-auto mb-4">
                  <span className="text-green-400 font-semibold">{item.step}</span>
                </div>
                <h3 className="text-lg font-medium mb-2">{item.title}</h3>
                <p className="text-gray-500 text-sm">{item.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-24 px-6">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-semibold mb-4">Peace of Mind</h2>
            <p className="text-gray-400">
              Find My integration means one less thing to worry about.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-6">
            {[
              'Locate ClearWay even when it is out of Bluetooth range',
              'Play a sound to find it in your home',
              'See the last known location if the battery is low',
              'Set up notifications when ClearWay is found',
              'Built into the device — no extra setup required',
              'Works with the vast Find My network'
            ].map((benefit, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: index * 0.05 }}
                className="flex items-center gap-4 p-4 rounded-xl bg-gray-800/30 border border-gray-800/50"
              >
                <div className="w-8 h-8 rounded-full bg-green-500/20 flex items-center justify-center flex-shrink-0">
                  <Check className="w-4 h-4 text-green-400" />
                </div>
                <span className="text-gray-300">{benefit}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 px-6 bg-[#0f0f0f]">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl font-semibold mb-6">
              Learn more about ClearWay
            </h2>
            <p className="text-gray-400 mb-8">
              Discover the team and mission behind ClearWay.
            </p>
            <Link
              to="/about"
              className="inline-flex items-center gap-2 px-8 py-4 rounded-full bg-white text-black font-medium hover:bg-gray-100 transition-all"
            >
              About Us
              <ArrowRight className="w-4 h-4" />
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default FindMyPage;
